import { ApolloClient, ApolloLink, InMemoryCache, Observable } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { env } from "../utils/env";
import { createUploadLink } from "apollo-upload-client";
import omitDeep from "omit-deep-lodash";
import { getUserManager } from "@axa-fr/react-oidc-core";
import i18next from "i18next";
import { getTenantId } from "providers/TenantProvider";
import { LANGUAGES_QUERY } from "utils/languages";
import { emptyObject } from "constants/defaultValues";
import { getFetchOperationName, getHeaderValue } from "utils/functions";
import { logRequestTrace } from "utils/diagnostics";
import { v4 as uuidv4 } from "uuid";

let access_token;
export function setAccessToken(accessToken) {
  access_token = accessToken;
}

const REQUEST_TRACE_STALL_THRESHOLD_MS = 30000;

const tracedFetch = (uri, options = {}) => {
  const startedAt = new Date();
  const startedAtMs = startedAt.getTime();
  const correlationId = getHeaderValue(options.headers, "nbb.correlation_id");
  const operationName = getFetchOperationName(options.body);
  let settled = false;

  const trace = { correlationId, operationName, startedAt, startedAtMs };
  logRequestTrace("info", "http.request.started", trace);

  const stallTimer = setTimeout(() => {
    if (settled) return;
    logRequestTrace("warn", "http.request.pending", trace);
  }, REQUEST_TRACE_STALL_THRESHOLD_MS);

  return Promise.resolve()
    .then(() => fetch(uri, options))
    .then(
      (response) => {
        settled = true;
        clearTimeout(stallTimer);
        logRequestTrace("info", "http.response", trace, { statusCode: response.status });
        return response;
      },
      (error) => {
        settled = true;
        clearTimeout(stallTimer);
        logRequestTrace("error", "http.failure", trace, {
          errorName: error.name,
          errorMessage: error.message,
        });
        throw error;
      }
    );
};

const httpLink = createUploadLink({
  uri: `${env.REACT_APP_GQL_HTTP_PROTOCOL}://${env.REACT_APP_GQL}/graphql`,
  fetch: tracedFetch,
  onError: onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      );
    if (networkError) console.log(`[Network error]: ${networkError}`);
  }),
});

const getToken = async () => {
  const userManager = getUserManager();
  const user = await userManager.getUser();
  if (user?.access_token) return user.access_token;

  return access_token;
};

const authLink = setContext(async (_, { headers }) => {
  const { languages } = cache.readQuery({ query: LANGUAGES_QUERY }) || emptyObject;
  const language = languages?.find((x) => x.code === i18next.language);

  const tid = getTenantId();
  const baseHeaders = { ...headers, "x-tenantid": tid };

  if (language?.culture) baseHeaders["Accept-Language"] = language.culture;
  if (env.REACT_APP_USE_CIAM?.toLowerCase() === "true") return { headers: baseHeaders };

  const token = await getToken();
  const authorization = token ? `Bearer ${token}` : "";
  return { headers: { ...baseHeaders, authorization } };
});

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = omitDeep(operation.variables, ["__typename"]);
  }
  return forward(operation);
});

const retryLink = new RetryLink({ delay: { initial: 200, max: 2000, jitter: true }, attempts: { max: 3 } });

const CIAM_RELOAD_ATTEMPTS_KEY = "vcp.ciam.authReloadAttempts";
const MAX_CIAM_RELOAD_ATTEMPTS = 3;
const ACTIVE_REQUEST_TRACES_KEY = "vcp.graphql.activeRequestTraces";
let isReloading = false;

const readActiveRequestTraces = () => {
  try {
    const traces = JSON.parse(window.sessionStorage.getItem(ACTIVE_REQUEST_TRACES_KEY) || "{}");
    return traces && typeof traces === "object" && !Array.isArray(traces) ? traces : {};
  } catch (error) {
    console.error("[Apollo trace] Could not read active request traces from session storage.", error);
    return {};
  }
};

const writeActiveRequestTraces = (traces) => {
  try {
    if (Object.keys(traces).length) {
      window.sessionStorage.setItem(ACTIVE_REQUEST_TRACES_KEY, JSON.stringify(traces));
    } else {
      window.sessionStorage.removeItem(ACTIVE_REQUEST_TRACES_KEY);
    }
  } catch (error) {
    console.error("[Apollo trace] Could not persist active request traces.", error);
  }
};

const removeActiveRequestTrace = (correlationId) => {
  const traces = readActiveRequestTraces();
  delete traces[correlationId];
  writeActiveRequestTraces(traces);
};

const interruptedRequestTraces = readActiveRequestTraces();
writeActiveRequestTraces({});
Object.values(interruptedRequestTraces).forEach((trace) => {
  logRequestTrace("warn", "request.interrupted", trace, {
    reason: "Page loaded again before the request completed.",
  });
});

const successfulOperationLink = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    if (result.data && !result.errors?.length) window.sessionStorage.removeItem(CIAM_RELOAD_ATTEMPTS_KEY);
    return result;
  })
);

const requestTraceLink = new ApolloLink((operation, forward) => {
  const correlationId = uuidv4();
  const startedAt = new Date();
  const startedAtMs = startedAt.getTime();
  const operationName = operation?.operationName || "";
  const trace = {
    correlationId,
    operationName,
    startedAt,
    startedAtMs,
  };

  operation.setContext(({ headers = {} }) => ({
    correlationId,
    requestTrace: trace,
    headers: { ...headers, "nbb.correlation_id": correlationId },
  }));
  const activeTraces = readActiveRequestTraces();
  activeTraces[correlationId] = trace;
  writeActiveRequestTraces(activeTraces);

  logRequestTrace("info", "request.started", trace);

  return new Observable((observer) => {
    let isTerminal = false;
    let isStallLogged = false;
    const stallTimer = setTimeout(() => {
      isStallLogged = true;
      logRequestTrace("warn", "request.pending", trace);
    }, REQUEST_TRACE_STALL_THRESHOLD_MS);
    const subscription = forward(operation).subscribe({
      next: (result) => {
        if (isTerminal) return;
        isTerminal = true;
        clearTimeout(stallTimer);
        removeActiveRequestTrace(correlationId);
        const response = operation.getContext().response;
        logRequestTrace(
          result.errors?.length ? "warn" : "info",
          result.errors?.length ? "request.graphql-error" : "request.completed",
          trace,
          {
            statusCode: response?.status || null,
            errorCount: result.errors?.length || 0,
          }
        );
        observer.next(result);
      },
      error: (error) => {
        isTerminal = true;
        clearTimeout(stallTimer);
        removeActiveRequestTrace(correlationId);
        const context = operation.getContext();
        if (!context.requestTraceFailureLogged) {
          logRequestTrace("error", "request.failed", trace, {
            statusCode: error.networkError?.statusCode || error.networkError?.status || null,
            errorName: error.name,
          });
        }
        observer.error(error);
      },
      complete: () => {
        isTerminal = true;
        clearTimeout(stallTimer);
        removeActiveRequestTrace(correlationId);
        observer.complete();
      },
    });

    return () => {
      clearTimeout(stallTimer);
      if (!isTerminal) {
        removeActiveRequestTrace(correlationId);
        logRequestTrace("warn", "request.cancelled", trace, { stalled: isStallLogged });
      }
      subscription.unsubscribe();
    };
  });
});

const authErrorLink = onError(({ networkError, operation }) => {
  if (networkError) {
    const { requestTrace } = operation.getContext();
    if (requestTrace)
      logRequestTrace("error", "request.failed", requestTrace, {
        statusCode: networkError.statusCode || networkError.status || null,
        errorName: networkError.name,
      });
    operation.setContext({ requestTraceFailureLogged: true });
  }

  if (env.REACT_APP_USE_CIAM?.toLowerCase() === "true" && networkError && !isReloading) {
    const isAuthError =
      networkError?.statusCode === 302 ||
      networkError?.statusCode === 401 ||
      (networkError.message?.includes("Failed to fetch") && operation?.operationName !== "IntrospectionQuery") ||
      (networkError.message?.includes("CORS") && operation?.operationName !== "IntrospectionQuery");

    if (isAuthError) {
      const storedAttempts = Number(window.sessionStorage.getItem(CIAM_RELOAD_ATTEMPTS_KEY));
      const reloadAttempts = Number.isInteger(storedAttempts) && storedAttempts >= 0 ? storedAttempts : 0;

      if (reloadAttempts >= MAX_CIAM_RELOAD_ATTEMPTS) return;

      window.sessionStorage.setItem(CIAM_RELOAD_ATTEMPTS_KEY, String(reloadAttempts + 1));
      isReloading = true;
      console.warn(
        `[Apollo CIAM] Authentication error detected (${
          networkError?.statusCode || networkError.message
        }) - reloading to follow redirect...`
      );
      window.location.reload();
    }
  }
});

const myAppLink = ApolloLink.from([
  requestTraceLink,
  omitTypenameLink,
  successfulOperationLink,
  authErrorLink,
  authLink,
  retryLink,
  httpLink,
]);

const cache = new InMemoryCache({
  typePolicies: {
    ApplicationParams: {
      keyFields: false,
    },
    ApplicationLoader: {
      keyFields: false,
    },
    Page: {
      keyFields: ["afterId", "sortBy", "direction", "pageSize"],
    },
    PartnerForCompanyData: {
      keyFields: false,
    },
    Quote: {
      keyFields: ["quoteId"],
    },
    QuoteAsset: {
      keyFields: ["quoteAssetId"],
    },
    QuoteProduct: {
      keyFields: ["quoteProductId"],
    },
    Asset: {
      keyFields: ["assetId"],
    },
    PriceCard: {
      keyFields: ["guid"],
    },
    ExchangeRate: {
      keyFields: false,
    },
    ListQuoteItem: {
      keyFields: ["quoteId"],
    },
    QuoteProductHistory: {
      keyFields: ["quoteProductId"],
    },
    LeasingPriceCard: {
      keyFields: ["priceCardId"],
    },
    File: {
      keyFields: false,
    },
    ProductSchedule: {
      keyFields: false,
    },
    ColumnDefinition: {
      keyFields: false,
    },
    ScheduleLine: {
      keyFields: false,
    },
    ScheduleLineValue: {
      keyFields: false,
    },
    InterestTypeDetail: {
      keyFields: false,
    },
    ContactPerson: {
      keyFields: ["webContactInfoId"],
    },
    DmsFile: {
      keyFields: ["sharePointUID"],
    },
    UserProfile: {
      keyFields: ["userId"],
    },
    Language: {
      keyFields: ["code"],
    },
    PublicDataCompletion: {
      keyFields: ["quoteId"],
    },
    PublicContractualDocuments: {
      keyFields: ["quoteId"],
    },
    ScoringQuestion: {
      keyFields: ["quoteId", "questionId"],
    },
    StringTranslations: {
      keyFields: ["languageCode"],
    },
    StringTranslation: {
      keyFields: ["key", "countryCode"],
    },
    RequiredDocument: {
      keyFields: ["quoteId", "id", "guarantorId"],
    },
    DealerDocument: {
      keyFields: ["dmsDocumentId"],
    },
  },
});

let apolloClient = null;
export const getApolloClient = () => {
  if (!apolloClient) {
    apolloClient = new ApolloClient({ link: myAppLink, cache });
  }
  return apolloClient;
};
